import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { alpha, useTheme } from '@mui/material/styles';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Dialog,
  DialogContent,
  Button,
  Chip,
  IconButton,
  InputBase,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import MicIcon from '@mui/icons-material/Mic';
import CheckIcon from '@mui/icons-material/Check';
import api from '../../utils/functions/api';
import ProjectPopup from '../Project/ProjectPopup';
import { stashAiPrefill } from '../../utils/functions/aiPrefill';
import { SIDEBAR_MODULE_TITLE, SIDEBAR_NAV_ITEMS } from '../../constants/sidebarNavConfig';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';

// พาเลตต์ของแชท = ธีมเดียวกับเว็บไซต์ (สีหลักจาก org theme, ฟอนต์ Prompt, พื้นขาว/เทา) ใช้เหมือนกันทั้งหน้าเต็มและแผงข้าง
// ชื่อ CLAUDE คงไว้เพื่อไม่ต้องแก้ทุกจุดที่อ้างอิง แต่ค่ามาจาก MUI theme แล้ว
function buildPalette(theme) {
  const { palette } = theme;
  return {
    bg: palette.background.paper,
    text: palette.text.primary,
    muted: palette.text.secondary,
    border: palette.divider,
    userBubble: alpha(palette.primary.main, 0.08),
    hover: palette.action.hover,
    accent: palette.primary.main,
    accentHover: palette.primary.dark,
    link: palette.info.main,
  };
}

// จำแชทที่เปิดค้างไว้ล่าสุด (เหมือน Claude Code ใน VSCode): ย้ายหน้า/เปิดแผงข้าง-หน้าเต็มสลับกันแล้วเห็นแชทเดิม
// ใช้ sessionStorage = หายเมื่อปิดแท็บ/เปิดเว็บใหม่ จึงเริ่มแชทใหม่เสมอ ไม่ใช่จำข้ามวัน
const ACTIVE_CHAT_KEY = 'ai_chat_active_id';
const readActiveChatId = () => {
  try { return sessionStorage.getItem(ACTIVE_CHAT_KEY); } catch (e) { return null; }
};
const writeActiveChatId = (id) => {
  try {
    if (id) sessionStorage.setItem(ACTIVE_CHAT_KEY, id);
    else sessionStorage.removeItem(ACTIVE_CHAT_KEY);
  } catch (e) { /* storage ใช้ไม่ได้ = ไม่จำ ไม่กระทบการใช้งาน */ }
};

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_DAYS = 7;
const MONTH_DAYS = 30;

// จัดกลุ่มประวัติแชทตามช่วงเวลาที่แก้ไขล่าสุด (เรียงใหม่→เก่าตามที่ backend ส่งมาอยู่แล้ว)
function groupChatsByDate(chats) {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const groups = [
    { label: 'วันนี้', maxAge: 0, items: [] },
    { label: 'เมื่อวาน', maxAge: 1, items: [] },
    { label: '7 วันที่ผ่านมา', maxAge: WEEK_DAYS, items: [] },
    { label: '30 วันที่ผ่านมา', maxAge: MONTH_DAYS, items: [] },
    { label: 'เก่ากว่านั้น', maxAge: Infinity, items: [] },
  ];
  chats.forEach((c) => {
    const age = Math.max(0, Math.floor((startOfToday.getTime() - new Date(c.updatedAt).getTime()) / DAY_MS) + 1);
    (groups.find((g) => age <= g.maxAge) || groups[groups.length - 1]).items.push(c);
  });

  return groups.filter((g) => g.items.length > 0);
}

const formatChatTime = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const sameDay = d.toDateString() === new Date().toDateString();

  return sameDay
    ? d.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short' });
};

const SpeechRecognitionCtor = typeof window !== 'undefined'
  ? window.SpeechRecognition || window.webkitSpeechRecognition
  : undefined;
const MAX_MESSAGE_CHARS = 2000; // ต้องตรงกับ MAX_MESSAGE_CHARS ใน backend/modules/aiChat/aiChat.service.js

const WAVE_BARS = 40;
const fmtTimer = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;

// แชทเก่าที่บันทึกคำตอบเป็น {"answer":"..."} ดิบไว้ (ก่อนแก้ฝั่ง backend) — แกะเอาแต่ข้อความตอนแสดงผล
// ข้อความปกติที่ไม่ขึ้นต้นด้วย {"answer" คืนเดิมทุกประการ
function cleanAnswer(content) {
  const text = String(content || '');
  if (!/^\s*\{\s*"answer"/.test(text)) return text;
  try {
    return String(JSON.parse(text).answer);
  } catch (error) {
    const match = text.match(/"answer"\s*:\s*"([\s\S]*)"\s*\}\s*$/);
    if (!match) return text;
    const escapes = { n: '\n', r: '\r', t: '\t' };
    return match[1].replace(/\\(["\\/nrt])/g, (_, c) => escapes[c] ?? c);
  }
}

const TOOL_LABELS = {
  searchProjects: 'ค้นหาโครงการ',
  getProjectDetail: 'รายละเอียดโครงการ',
  getProjectBudgets: 'งบประมาณรายหมวด',
  analyzeProject: 'วิเคราะห์โครงการ',
  analyzeExpenses: 'วิเคราะห์การเบิกจ่าย',
  analyzePayments: 'วิเคราะห์ใบเตรียมจ่าย',
  analyzeRecurringExpenses: 'รายการเบิกประจำ',
  analyzeLeave: 'วิเคราะห์การลา',
  analyzeOvertime: 'วิเคราะห์โอที',
  analyzeAttendance: 'วิเคราะห์การลงเวลา',
  analyzePayroll: 'วิเคราะห์เงินเดือน',
  analyzeEmployees: 'ภาพรวมพนักงาน',
  analyzePayouts: 'วิเคราะห์การจ่ายเงินจริง',
  analyzeDebtNotes: 'วิเคราะห์เจ้าหนี้/ลูกหนี้',
  analyzeCustomers: 'วิเคราะห์ลูกค้า',
  analyzeOrganization: 'ภาพรวมองค์กร/ผู้ใช้',
  analyzeMe: 'สรุปข้อมูลของฉัน',
  analyzeMarketing: 'วิเคราะห์โอกาสทางธุรกิจ',
  listMarketingOpportunities: 'ค้นโอกาสทางธุรกิจ',
  analyzeMarketingCheckins: 'วิเคราะห์เช็กอินการตลาด',
  getApproverSettings: 'ตั้งค่าผู้อนุมัติ',
  describeDataset: 'ดูรายละเอียดตาราง',
  getProjectWorkforce: 'พนักงานที่ลงโครงการ',
  portfolioSummary: 'วิเคราะห์ภาพรวม',
  countProjectsByStatus: 'นับโครงการตามสถานะ',
  listProjectData: 'ดึงข้อมูลตาราง',
  aggregateProjectData: 'สรุปยอดตามกลุ่ม',
  getUsageGuide: 'ดูวิธีใช้งานเว็บ',
};

// หน้าแชทถาม AI เรื่องข้อมูลในระบบ (ข้อมูลที่ผูกกับโครงการ) — ทุกคนที่ login ใช้ได้ แต่ฝั่ง backend (modules/aiChat)
// ให้ AI ตอบเฉพาะข้อมูลของเมนูย่อยที่ผู้ถามมีสิทธิ์ โดยเลือกเรียก tool แบบ read-only แล้วเก็บประวัติแชทไว้ต่อผู้ใช้
// active = แผงกำลังแสดงอยู่ไหม (หน้าเต็มเป็น true เสมอ) — ใช้หยุดไมค์เมื่อแผงถูกซ่อน
// compact = โหมดแผงข้าง (components/AiChatPanel.js): ไม่มีรายการแชทด้านซ้าย ย้ายไปเป็นปุ่ม "ประวัติ" ใน header แทน
// เลขที่โครงการในคำตอบ (เช่น JS_26053, JSID_26044) กดเปิด popup โครงการได้ — ตรวจสิทธิ์ที่ backend ตอน resolve
const PROJECT_NO_RE = /(\b[A-Z]{2,5}_\d{3,6}\b|\/(?:profile|project|humen|finance|sale|customer|director|management|accounting|marketing|monitor)(?:\/[A-Za-z0-9_-]+)+)/g;

// เมนู sidebar ทั้งหมด (ชื่อโมดูล + เมนูย่อย) ส่งไปกับคำถามเพื่อให้ AI สอนวิธีใช้งานตามเมนูที่มีจริงตอนนี้ — backend กรองตามสิทธิ์ผู้ถามเองอีกชั้น
// เพิ่ม/ย้าย/เปลี่ยนชื่อเมนูใน sidebarNavConfig.js แล้ว AI รู้ทันที ไม่ต้องบันทึกวิธีใช้ไว้ที่ไหน
const SIDEBAR_MENUS_FOR_AI = (() => {
  const byModule = new Map();
  SIDEBAR_NAV_ITEMS.forEach((item) => {
    const entries = item.sub && item.sub.length ? item.sub : [item];
    if (!byModule.has(item.name)) byModule.set(item.name, new Map());
    entries.forEach((e) => byModule.get(item.name).set(e.href, e.title));
  });

  return Array.from(byModule, ([name, items]) => ({
    title: SIDEBAR_MODULE_TITLE[name] || name,
    items: Array.from(items, ([href, title]) => ({ href, title })),
  }));
})();

function linkifyProjects(text, onOpen, onNavigate) {
  const parts = String(text).split(PROJECT_NO_RE);
  return parts.map((part, idx) => {
    // split ด้วย capture group: ตำแหน่งคี่ = เลขที่โครงการ
    if (idx % 2 === 0) return part;
    // path ของหน้าในเว็บ (เช่น /finance/expenses) → กดแล้วไปหน้านั้น ส่วนที่เหลือคือเลขที่โครงการ → เปิด popup
    const open = part.startsWith('/') ? onNavigate : onOpen;
    return (
      // eslint-disable-next-line react/no-array-index-key
      <Box
        // eslint-disable-next-line react/no-array-index-key
        key={idx}
        component="span"
        role="button"
        tabIndex={0}
        onClick={() => open(part)}
        onKeyDown={(e) => { if (e.key === 'Enter') open(part); }}
        sx={{
          color: 'info.main', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline dotted', '&:hover': { textDecoration: 'underline' },
        }}
      >
        {part}
      </Box>
    );
  });
}

// marker [[open:/path|ชื่อหน้า]] ที่ backend ต่อท้ายคำตอบเมื่อผู้ใช้สั่ง "เปิดหน้า ..." — แยกออกจากข้อความที่โชว์
const OPEN_MARKER_RE = /\n?\[\[open:(\/[A-Za-z0-9/_-]*)\|([^|\]]*)(?:\|([^\]]*))?\]\]\s*$/;

function splitOpenMarker(text) {
  const match = String(text).match(OPEN_MARKER_RE);
  if (!match) return { text, target: null };
  let prefill = null;
  try {
    prefill = match[3] ? JSON.parse(decodeURIComponent(match[3])) : null;
  } catch (e) { prefill = null; }
  return { text: String(text).replace(OPEN_MARKER_RE, ''), target: { href: match[1], title: match[2], prefill } };
}

// นำทางไปหน้าที่ AI สั่ง — ถ้ามีค่าที่กรอกให้ เก็บไว้ให้ฟอร์มปลายทางอ่าน (ตอนนี้รองรับฟอร์มขอลา)
function goTarget(history, target) {
  if (target.prefill && target.href === '/profile/leave/add') stashAiPrefill('leave_request', target.prefill);
  history.push(target.href);
}

export default function AiChat({ compact, onClose, active }) {
  const theme = useTheme();
  const CLAUDE = buildPalette(theme);
  const apiBase = String(process.env.REACT_APP_API_URL || '').trim();

  const history = useHistory();
  const [popupProjectId, setPopupProjectId] = useState(null);
  const [chats, setChats] = useState([]);
  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const openProject = useCallback(async (number) => {
    const { data } = await api.get(`${apiBase}/ai-chat/resolve-project`, { params: { number } });
    const id = data?.id || data?.data?.id;
    if (id) setPopupProjectId(id);
  }, []);
  const [providers, setProviders] = useState([]);
  const [providerKey, setProviderKey] = useState('');
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [historyAnchor, setHistoryAnchor] = useState(null);
  const [historySearch, setHistorySearch] = useState('');
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const [starters, setStarters] = useState([]);
  const [listening, setListening] = useState(false);
  const [voiceSecs, setVoiceSecs] = useState(0);
  const [interimText, setInterimText] = useState('');
  const [levels, setLevels] = useState(() => Array(WAVE_BARS).fill(0));
  const voiceRef = useRef(null); // { stream, ctx, raf, timer, base, finalText, interim, stopped }

  const loadChats = useCallback(async () => {
    const { data } = await api.get(`${apiBase}/ai-chat/chats`);
    const list = Array.isArray(data) ? data : [];
    setChats(list);

    return list;
  }, [apiBase]);

  // api.js แสดง error dialog ให้อยู่แล้ว — .catch ตรงนี้แค่กัน unhandled rejection ใน console
  useEffect(() => {
    loadChats().catch(() => {});
    // คำถามแนะนำตอนเปิดแชทใหม่ — backend เลือกตามเมนูที่ผู้ใช้มีสิทธิ์
    api.get(`${apiBase}/ai-chat/starters`).then(({ data }) => setStarters(Array.isArray(data) ? data : [])).catch(() => {});
    (async () => {
      // รายชื่ออย่างเดียว (ไม่ใช่ /api-provider ของหน้านักพัฒนา ที่ส่งรายละเอียด key/โควต้า) เพื่อให้พนักงานทั่วไปใช้แชทได้โดยไม่เห็นข้อมูล key
      const { data } = await api.get(`${apiBase}/ai-chat/providers`);
      const list = Array.isArray(data) ? data : [];
      setProviders(list);
      setProviderKey((prev) => prev || list[0]?.provider_key || '');
    })().catch(() => {});
  }, [apiBase, loadChats]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  // เขียนเฉพาะตอนมีแชทจริง (ตอน mount chatId ยัง null — ห้ามเขียนทับค่าที่จำไว้ก่อนอ่านคืน); ล้างค่าที่ newChat
  useEffect(() => {
    if (chatId) writeActiveChatId(chatId);
  }, [chatId]);

  const openChat = async (id) => {
    setHistoryAnchor(null);
    const { data } = await api.get(`${apiBase}/ai-chat/chats/${id}`);
    setChatId(data.id);
    setMessages(data.messages || []);
    if (data.provider_key) setProviderKey(data.provider_key);
  };

  // ตอน mount และทุกครั้งที่หน้า/แผงถูกเปิดขึ้นมา: ถ้าแชทที่จำไว้ (อาจถูกเปลี่ยนจากอีกที่ — แผงข้าง↔หน้าเต็ม) ไม่ตรงกับที่แสดงอยู่ ให้โหลดตามนั้น
  // เช็คกับรายการแชทก่อนเสมอ กันยิงขอแชทที่ถูกลบไปแล้วแล้วเด้ง error dialog
  useEffect(() => {
    if (active === false || sending) return;
    const saved = readActiveChatId();
    if (!saved || saved === chatId) return;
    loadChats()
      .then((list) => {
        if (list.some((c) => c.id === saved)) return openChat(saved);
        writeActiveChatId(null);

        return null;
      })
      .catch(() => {});
  }, [active]);

  const newChat = () => {
    writeActiveChatId(null);
    setHistoryAnchor(null);
    setChatId(null);
    setMessages([]);
  };

  const removeChat = async (id) => {
    if (!(await confirmAsync('ลบแชทนี้ใช่ไหม?'))) return;
    await api.delete(`${apiBase}/ai-chat/chats/${id}`);
    if (id === chatId) newChat();
    loadChats();
  };

  // textOverride = ข้อความจากเสียงที่ส่งอัตโนมัติ (ตอนนั้น state input ใน closure ของ recognition ยังเป็นค่าเก่า)
  const send = async (textOverride) => {
    const text = (typeof textOverride === 'string' ? textOverride : input).trim();
    if (!text || sending) return;
    if (!providerKey) {
      showAlert('ยังไม่มี AI provider ที่พร้อมใช้งาน — แจ้งผู้ดูแลระบบให้ตั้งค่าที่หน้า "นักพัฒนา"');
      return;
    }
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setSending(true);
    try {
      const { data } = await api.post(`${apiBase}/ai-chat/ask`, {
        chatId, message: text, providerKey, menus: SIDEBAR_MENUS_FOR_AI,
      });
      setChatId(data.id);
      setMessages(data.messages || []);
      loadChats();
      // สั่ง "เปิดหน้า ..." → นำทางไปหน้านั้นทันที (เฉพาะคำตอบใหม่ ไม่ทำตอนเปิดดูประวัติแชทเก่า)
      const last = (data.messages || [])[(data.messages || []).length - 1];
      const nav = last?.role === 'assistant' ? splitOpenMarker(last.content).target : null;
      if (nav) goTarget(history, nav);
    } catch (error) {
      // api.js แสดง error dialog ให้อยู่แล้ว — ถอยข้อความที่เพิ่งใส่ให้ผู้ใช้ส่งซ้ำได้
      setMessages((prev) => prev.slice(0, -1));
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  // ── สั่งงานด้วยเสียง (แบบเดียวกับ dictation ของ Claude) ───────────────────────────────────────────────────
  // กดไมค์ → แถบอัดเสียง (คลื่นเสียงสด + เวลา) → ✕ ยกเลิก / ✓ ยืนยัน แล้วข้อความเข้าช่องพิมพ์ให้แก้ก่อนส่งเอง (ไม่ส่งอัตโนมัติ)
  // ถอดความด้วย Web Speech API ของเบราว์เซอร์ (Chrome/Edge/Safari, HTTPS หรือ localhost) — ฟังต่อเนื่องจนกว่าจะกด ✓/✕
  const teardownVoice = useCallback(() => {
    const v = voiceRef.current;
    if (!v) return;
    v.stopped = true;
    clearInterval(v.timer);
    cancelAnimationFrame(v.raf);
    const rec = recognitionRef.current;
    if (rec) {
      rec.onresult = null;
      rec.onerror = null;
      rec.onend = null;
      try { rec.abort(); } catch (e) { /* ปิดไปแล้ว */ }
    }
    recognitionRef.current = null;
    v.stream?.getTracks().forEach((t) => t.stop());
    v.ctx?.close().catch(() => {});
    voiceRef.current = null;
    setListening(false);
    setInterimText('');
    setVoiceSecs(0);
    setLevels(Array(WAVE_BARS).fill(0));
  }, []);

  const cancelVoice = useCallback(() => teardownVoice(), [teardownVoice]);

  const confirmVoice = () => {
    const v = voiceRef.current;
    if (!v) return;
    const text = `${v.base}${v.finalText}${v.pending || ''}${v.interim}`.trim();
    teardownVoice();
    if (text) setInput(text.slice(0, MAX_MESSAGE_CHARS));
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const startVoice = async () => {
    if (listening) return;
    if (!SpeechRecognitionCtor) {
      showAlert('เบราว์เซอร์นี้ไม่รองรับการสั่งด้วยเสียง — ใช้ Chrome, Edge หรือ Safari');
      return;
    }
    const v = {
      base: input.trim() ? `${input.trim()} ` : '', finalText: '', interim: '', stopped: false,
    };
    voiceRef.current = v;
    setListening(true);
    setVoiceSecs(0);
    setInterimText(input);
    v.timer = setInterval(() => setVoiceSecs((n) => n + 1), 1000);

    // คลื่นเสียงสดจากไมโครโฟน (ถ้าเปิดไม่ได้ก็ยังถอดเสียงได้ แค่ไม่มีคลื่น)
    try {
      v.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (v.stopped) { v.stream.getTracks().forEach((t) => t.stop()); return; }
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      v.ctx = new AudioCtx();
      const analyser = v.ctx.createAnalyser();
      analyser.fftSize = 128;
      v.ctx.createMediaStreamSource(v.stream).connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length / 255;
        setLevels((prev) => [...prev.slice(1), Math.min(1, avg * 3)]);
        v.raf = requestAnimationFrame(tick);
      };
      tick();
    } catch (error) {
      if (error?.name === 'NotAllowedError') {
        teardownVoice();
        showAlert('ไม่ได้รับอนุญาตให้ใช้ไมโครโฟน — กดไอคอนกุญแจข้าง URL แล้วอนุญาตไมโครโฟนให้เว็บนี้');
        return;
      }
      if (error?.name === 'NotFoundError') {
        teardownVoice();
        showAlert('ไม่พบไมโครโฟนในเครื่องนี้');
        return;
      }
    }
    if (v.stopped) return;

    const startRecognition = () => {
      const recognition = new SpeechRecognitionCtor();
      recognition.lang = 'th-TH';
      recognition.interimResults = true;
      recognition.continuous = true;
      recognition.onresult = (event) => {
        let fin = '';
        let interim = '';
        for (let i = 0; i < event.results.length; i += 1) {
          if (event.results[i].isFinal) fin += event.results[i][0].transcript;
          else interim += event.results[i][0].transcript;
        }
        v.pending = fin; // ผลของ session นี้ (นับใหม่ทุกครั้งที่ recognition เริ่มใหม่)
        v.interim = interim;
        setInterimText(`${v.base}${v.finalText}${fin}${interim}`);
      };
      recognition.onerror = (event) => {
        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          teardownVoice();
          showAlert('ไม่ได้รับอนุญาตให้ใช้ไมโครโฟน — กดไอคอนกุญแจข้าง URL แล้วอนุญาตไมโครโฟนให้เว็บนี้');
        }
      };
      // Chrome ตัดการฟังเองเมื่อเงียบนาน/ครบเวลา — เก็บผลรอบนี้แล้วเริ่มฟังต่อ จนกว่าผู้ใช้จะกด ✓/✕
      recognition.onend = () => {
        if (v.stopped) return;
        // เก็บทั้งส่วนที่ยืนยันแล้วและส่วนที่ยังเป็น interim ไว้ — ห้ามทิ้ง (ไม่งั้นพอ Chrome ตัดตอนเงียบ ข้อความที่พูดไปจะหาย)
        v.finalText += `${v.pending || ''}${v.interim || ''}`;
        v.pending = '';
        v.interim = '';
        try { startRecognition(); } catch (e) { /* เริ่มซ้ำไม่ได้ ปล่อยให้ผู้ใช้กด ✓ */ }
      };
      recognitionRef.current = recognition;
      recognition.start();
    };
    try {
      startRecognition();
    } catch (error) {
      teardownVoice();
    }
  };

  // ออกจากหน้า/แผงถูกซ่อน = เลิกอัดทันที (แผงแค่ซ่อนไม่ได้ถอด component — ไมค์จะค้างถ้าไม่หยุดเอง)
  useEffect(() => {
    if (!active) teardownVoice();
  }, [active, teardownVoice]);
  useEffect(() => () => teardownVoice(), [teardownVoice]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  // ประวัติแชท: ช่องค้นหา + จัดกลุ่มตามวัน + แถวที่เลือกอยู่มีแถบสีหลักด้านซ้าย — ใช้ร่วมกันทั้งแถบข้างหน้าเต็มและ popover ของแผงข้าง
  const renderChatList = () => {
    const keyword = historySearch.trim().toLowerCase();
    const filtered = keyword ? chats.filter((c) => (c.title || '').toLowerCase().includes(keyword)) : chats;
    const groups = groupChatsByDate(filtered);

    return (
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 1.5, pb: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{
              px: 1.25, py: 0.25, borderRadius: 2, bgcolor: CLAUDE.hover, color: CLAUDE.muted,
            }}
          >
            <SearchIcon sx={{ fontSize: 18 }} />
            <InputBase
              fullWidth
              placeholder="ค้นหาแชท"
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              sx={{ fontSize: '0.82rem', color: CLAUDE.text }}
            />
          </Stack>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pb: 1 }}>
          {groups.length === 0 && (
            <Stack alignItems="center" spacing={0.75} sx={{ py: 5, color: CLAUDE.muted }}>
              <HistoryIcon sx={{ fontSize: 32, opacity: 0.5 }} />
              <Typography sx={{ fontSize: '0.82rem' }}>
                {keyword ? 'ไม่พบแชทที่ค้นหา' : 'ยังไม่มีประวัติแชท'}
              </Typography>
            </Stack>
          )}
          {groups.map((g) => (
            <Box key={g.label} sx={{ mb: 1 }}>
              <Typography sx={{
                px: 1, py: 0.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: 0.3, color: CLAUDE.muted,
              }}
              >
                {g.label}
              </Typography>
              {g.items.map((c) => {
                const selected = c.id === chatId;

                return (
                  <Stack
                    key={c.id}
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    onClick={() => openChat(c.id)}
                    sx={{
                      position: 'relative',
                      px: 1.25,
                      py: 0.75,
                      mb: 0.25,
                      borderRadius: 2,
                      cursor: 'pointer',
                      bgcolor: selected ? CLAUDE.userBubble : 'transparent',
                      '&:hover': { bgcolor: selected ? CLAUDE.userBubble : CLAUDE.hover },
                      '&:hover .delete-btn': { visibility: 'visible' },
                      '&::before': selected ? {
                        content: '""', position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, borderRadius: 2, bgcolor: CLAUDE.accent,
                      } : {},
                    }}
                  >
                    <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: selected ? CLAUDE.accent : CLAUDE.muted, flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        noWrap
                        sx={{ fontSize: '0.84rem', fontWeight: selected ? 600 : 400, color: CLAUDE.text }}
                      >
                        {c.title}
                      </Typography>
                      <Typography noWrap sx={{ fontSize: '0.68rem', color: CLAUDE.muted }}>
                        {formatChatTime(c.updatedAt)}
                      </Typography>
                    </Box>
                    <Tooltip title="ลบแชท">
                      <IconButton
                        size="small"
                        className="delete-btn"
                        sx={{ visibility: 'hidden', color: CLAUDE.muted, '&:hover': { color: 'error.main' } }}
                        onClick={(e) => { e.stopPropagation(); removeChat(c.id); }}
                      >
                        <DeleteIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  // ตัวเลือก provider อยู่มุมซ้ายล่างของกล่องพิมพ์ (เหมือนตัวเลือกโมเดลของ Claude) แบบไม่มีกรอบ
  const providerSelect = (
    <Select
      variant="standard"
      disableUnderline
      value={providerKey}
      onChange={(e) => setProviderKey(e.target.value)}
      displayEmpty
      renderValue={(v) => providers.find((p) => p.provider_key === v)?.display_name || 'เลือก AI'}
      sx={{ fontSize: '0.8rem', color: CLAUDE.muted, '& .MuiSelect-select': { py: 0.5 } }}
    >
      {providers.map((p) => (
        <MenuItem key={p.provider_key} value={p.provider_key}>{p.display_name}</MenuItem>
      ))}
    </Select>
  );

  return (
    <Box sx={{ display: 'flex', gap: 2, height: compact ? '100%' : 'calc(100vh - 140px)' }}>
      {!compact && (
        <Paper
          variant="outlined"
          sx={{
            width: 272, display: 'flex', flexDirection: 'column', flexShrink: 0, bgcolor: CLAUDE.bg, borderColor: CLAUDE.border,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 1.75, pb: 1 }}>
            <HistoryIcon sx={{ fontSize: 20, color: CLAUDE.accent }} />
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: CLAUDE.text }}>ประวัติแชท</Typography>
          </Stack>
          <Box sx={{ px: 1.5, pb: 1.25 }}>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              startIcon={<AddIcon />}
              onClick={newChat}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              แชทใหม่
            </Button>
          </Box>
          {renderChatList()}
        </Paper>
      )}

      <Paper
        variant={compact ? 'elevation' : 'outlined'}
        elevation={0}
        square={compact}
        sx={{
          flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, bgcolor: CLAUDE.bg, borderColor: CLAUDE.border,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ px: 2, py: 1.25 }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <Box sx={{
              width: 28, height: 28, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: CLAUDE.userBubble, color: CLAUDE.accent,
            }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 17 }} />
            </Box>
            <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: CLAUDE.text }} noWrap>
              Coreplan AI
            </Typography>
          </Stack>
          {compact && (
            <Stack direction="row" alignItems="center" spacing={0.25} sx={{ color: CLAUDE.muted }}>
              <Tooltip title="แชทใหม่">
                <IconButton size="small" color="inherit" onClick={newChat}><AddIcon /></IconButton>
              </Tooltip>
              <Tooltip title="ประวัติแชท">
                <IconButton size="small" color="inherit" onClick={(e) => setHistoryAnchor(e.currentTarget)}>
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="ปิด">
                <IconButton size="small" color="inherit" onClick={onClose}><CloseIcon /></IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 1 }}>
          {messages.length === 0 && (
            <Box sx={{ mt: 6, textAlign: 'center' }}>
              <AutoAwesomeIcon sx={{ fontSize: 40, color: CLAUDE.accent }} />
              <Typography sx={{
                fontSize: '1.35rem', fontWeight: 600, color: CLAUDE.text, mt: 1.5, mb: 3, lineHeight: 1.3,
              }}
              >
                อยากรู้เรื่องอะไรครับ?
              </Typography>
              <Stack spacing={1} alignItems="stretch">
                {starters.map((s) => (
                  <Box
                    key={s}
                    onClick={() => { setInput(s); inputRef.current?.focus(); }}
                    sx={{
                      px: 2,
                      py: 1,
                      border: `1px solid ${CLAUDE.border}`,
                      borderRadius: 3,
                      bgcolor: CLAUDE.bg,
                      color: CLAUDE.text,
                      fontSize: '0.85rem',
                      textAlign: 'left',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: CLAUDE.hover, borderColor: CLAUDE.accent },
                    }}
                  >
                    {s}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
          {messages.map((m, i) => (
            m.role === 'user' ? (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={i} sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2.5 }}>
                <Box sx={{
                  maxWidth: '85%', px: 2, py: 1.25, borderRadius: '18px', bgcolor: CLAUDE.userBubble, color: CLAUDE.text,
                }}
                >
                  <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.92rem' }}>
                    {m.content}
                  </Typography>
                </Box>
              </Box>
            ) : (
              // คำตอบของ AI ไม่มีกรอบ/พื้นหลัง วางเป็นข้อความธรรมดาเหมือน Claude
              // eslint-disable-next-line react/no-array-index-key
              <Box key={i} sx={{ mb: 3, color: CLAUDE.text }}>
                <Typography sx={{
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.92rem', lineHeight: 1.7,
                }}
                >
                  {linkifyProjects(splitOpenMarker(cleanAnswer(m.content)).text, openProject, (path) => history.push(path))}
                </Typography>
                {splitOpenMarker(cleanAnswer(m.content)).target && (
                  <Chip
                    size="small"
                    clickable
                    label={`เปิดหน้า ${splitOpenMarker(cleanAnswer(m.content)).target.title}`}
                    onClick={() => goTarget(history, splitOpenMarker(cleanAnswer(m.content)).target)}
                    sx={{ mt: 0.75 }}
                  />
                )}
                {i === messages.length - 1 && !sending && m.choices?.length > 0 && (
                  <Stack spacing={0.75} sx={{ mt: 1.25, alignItems: 'flex-start' }}>
                    <Typography variant="caption" sx={{ color: CLAUDE.muted }}>
                      เลือกตัวเลือก หรือพิมพ์คำตอบของคุณเองในช่องด้านล่าง
                    </Typography>
                    {m.choices.map((c) => (
                      <Chip
                        key={c.label}
                        clickable
                        color="primary"
                        variant="outlined"
                        label={c.label}
                        onClick={() => send(c.message || c.label)}
                        sx={{
                          height: 'auto', py: 0.75, fontSize: '0.82rem', justifyContent: 'flex-start', '& .MuiChip-label': { whiteSpace: 'normal' },
                        }}
                      />
                    ))}
                  </Stack>
                )}
                {i === messages.length - 1 && !sending && m.suggestions?.length > 0 && (
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mt: 1.25 }}>
                    {m.suggestions.map((sg) => (
                      <Chip
                        key={sg}
                        size="small"
                        clickable
                        variant="outlined"
                        label={sg}
                        onClick={() => send(sg)}
                        sx={{
                          height: 'auto', py: 0.5, fontSize: '0.78rem', borderColor: CLAUDE.border, color: CLAUDE.text, '& .MuiChip-label': { whiteSpace: 'normal' },
                        }}
                      />
                    ))}
                  </Stack>
                )}
                {m.tools?.length > 0 && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: CLAUDE.muted }}>
                    ใช้: {m.tools.map((t) => TOOL_LABELS[t] || t).join(', ')}
                  </Typography>
                )}
                <AutoAwesomeIcon sx={{
                  display: 'block', mt: 1, fontSize: 18, color: CLAUDE.accent,
                }}
                />
              </Box>
            )
          ))}
          {sending && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: CLAUDE.muted, mb: 2 }}>
              <AutoAwesomeIcon sx={{
                fontSize: 20,
                color: CLAUDE.accent,
                animation: 'aiChatPulse 1.2s ease-in-out infinite',
                '@keyframes aiChatPulse': { '0%, 100%': { opacity: 0.35 }, '50%': { opacity: 1 } },
              }}
              />
              <Typography variant="body2">กำลังค้นหาข้อมูล...</Typography>
            </Stack>
          )}
          <div ref={bottomRef} />
        </Box>

        <Box sx={{ px: 2, pb: 1.5, pt: 0.5 }}>
          {/* กล่องพิมพ์แบบการ์ดมน: ช่องพิมพ์ด้านบน, ตัวเลือก AI ซ้ายล่าง, ปุ่มส่งกลมขวาล่าง */}
          <Box sx={{
            bgcolor: CLAUDE.bg,
            border: `1px solid ${CLAUDE.border}`,
            borderRadius: '20px',
            px: 1.75,
            pt: 1.25,
            pb: 0.75,
            boxShadow: '0 1px 3px rgba(17,24,39,0.08)',
            '&:focus-within': { borderColor: CLAUDE.accent },
          }}
          >
            {listening ? (
              <Box
                onKeyDown={(e) => {
                  if (e.key === 'Escape') cancelVoice();
                  if (e.key === 'Enter') confirmVoice();
                }}
                sx={{ py: 0.5 }}
              >
                <InputBase
                  fullWidth
                  multiline
                  maxRows={8}
                  readOnly
                  value={interimText}
                  placeholder="กำลังฟัง... พูดได้เลย"
                  sx={{ fontSize: '0.92rem', color: CLAUDE.text, mb: 0.75 }}
                />
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Tooltip title="ยกเลิก (Esc)">
                    <IconButton
                      autoFocus
                      onClick={cancelVoice}
                      sx={{ width: 34, height: 34, bgcolor: CLAUDE.userBubble, color: CLAUDE.text }}
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Box sx={{
                    flex: 1, height: 34, display: 'flex', alignItems: 'center', gap: '2px', overflow: 'hidden',
                  }}
                  >
                    {levels.map((lv, idx) => (
                      <Box
                        // eslint-disable-next-line react/no-array-index-key
                        key={idx}
                        sx={{
                          flex: 1, minWidth: 2, maxWidth: 5, borderRadius: 2, bgcolor: CLAUDE.text, opacity: 0.75, height: `${Math.max(8, lv * 100)}%`, transition: 'height 80ms linear',
                        }}
                      />
                    ))}
                  </Box>
                  <Typography sx={{
                    fontSize: '0.8rem', color: CLAUDE.muted, fontVariantNumeric: 'tabular-nums', minWidth: 34, textAlign: 'right',
                  }}
                  >
                    {fmtTimer(voiceSecs)}
                  </Typography>
                  <Tooltip title="ใช้ข้อความนี้ (Enter)">
                    <IconButton
                      onClick={confirmVoice}
                      sx={{
                        width: 34, height: 34, bgcolor: CLAUDE.accent, color: '#fff', '&:hover': { bgcolor: CLAUDE.accentHover },
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            ) : (
              <>
            <InputBase
              fullWidth
              multiline
              maxRows={8}
              inputProps={{ maxLength: MAX_MESSAGE_CHARS }}
              inputRef={inputRef}
              placeholder="ถามเรื่องข้อมูลโครงการ... หรือกดไมค์เพื่อพูด"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
              sx={{ fontSize: '0.92rem', color: CLAUDE.text }}
            />
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5} sx={{ mt: 0.5 }}>
              {providerSelect}
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {SpeechRecognitionCtor && (
                  <>
                    <Tooltip title="สั่งด้วยเสียง (ภาษาไทย)">
                      <span>
                        <IconButton
                          onClick={startVoice}
                          disabled={sending}
                          sx={{ width: 32, height: 32, color: CLAUDE.muted, '&:hover': { bgcolor: CLAUDE.userBubble } }}
                        >
                          <MicIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}
              <IconButton
                onClick={() => send()}
                disabled={sending || !input.trim()}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: CLAUDE.accent,
                  color: '#fff',
                  '&:hover': { bgcolor: CLAUDE.accentHover },
                  '&.Mui-disabled': { bgcolor: CLAUDE.hover, color: CLAUDE.muted },
                }}
              >
                <ArrowUpwardIcon sx={{ fontSize: 18 }} />
              </IconButton>
              </Stack>
            </Stack>
              </>
            )}
          </Box>
          <Typography sx={{
            mt: 0.75, textAlign: 'center', fontSize: '0.7rem', color: CLAUDE.muted,
          }}
          >
            AI อาจตอบผิดพลาด ควรตรวจสอบข้อมูลสำคัญอีกครั้ง
          </Typography>
        </Box>
      </Paper>

      <Popover
        open={compact && Boolean(historyAnchor)}
        anchorEl={historyAnchor}
        onClose={() => setHistoryAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { borderRadius: 3, mt: 0.5, boxShadow: 6 } }}
      >
        <Box sx={{
          width: 320, maxHeight: 440, display: 'flex', flexDirection: 'column', pt: 1.5,
        }}
        >
          {renderChatList()}
        </Box>
      </Popover>
      <Dialog
        open={!!popupProjectId}
        onClose={() => setPopupProjectId(null)}
        maxWidth="xl"
        fullWidth
        sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
        PaperProps={{ sx: { mt: '40px', maxHeight: 'calc(100vh - 56px)', minWidth: '50vw' } }}
      >
        <DialogContent sx={{ p: 0 }}>
          {popupProjectId && <ProjectPopup propId={popupProjectId} onClose={() => setPopupProjectId(null)} />}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

AiChat.propTypes = {
  compact: PropTypes.bool,
  onClose: PropTypes.func,
  active: PropTypes.bool,
};

AiChat.defaultProps = {
  compact: false,
  onClose: () => {},
  active: true,
};
